'use client'

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { AppleHelloEnglishEffect, AppleHelloVietnameseEffect } from '@/components/ui/apple-hello-effect'
import { Button } from '@/components/ui/button'
import { SkipForward, Star, Trophy } from 'lucide-react'
import { cn } from '@/lib/utils'

interface AppleHelloIntroProps {
  onComplete?: () => void
  onSkip?: () => void
  showSkipButton?: boolean
  language?: 'english' | 'vietnamese' | 'both'
  className?: string
}

export function AppleHelloIntro({
  onComplete,
  onSkip,
  showSkipButton = true,
  language = 'english',
  className
}: AppleHelloIntroProps) {
  const [currentPhase, setCurrentPhase] = useState<'loading' | 'hello' | 'vietnamese' | 'complete'>('loading')
  const [achievementUnlocked, setAchievementUnlocked] = useState(false)
  const [experiencePoints, setExperiencePoints] = useState(0)

  useEffect(() => {
    // Start the intro sequence
    const timer = setTimeout(() => {
      setCurrentPhase('hello')
    }, 500)

    return () => clearTimeout(timer)
  }, [])

  const handleHelloComplete = () => {
    // Award XP for completing hello animation
    setExperiencePoints(50)
    setAchievementUnlocked(true)

    if (language === 'both') {
      setCurrentPhase('vietnamese')
    } else {
      setCurrentPhase('complete')
      setTimeout(() => {
        onComplete?.()
      }, 1500) // Increased delay for smoother transition
    }
  }

  const handleVietnameseComplete = () => {
    // Award bonus XP for bilingual experience
    setExperiencePoints(100)
    setCurrentPhase('complete')
    setTimeout(() => {
      onComplete?.()
    }, 1500) // Increased delay for smoother transition
  }

  const handleSkip = () => {
    setCurrentPhase('complete')
    onSkip?.()
  }

  return (
    <div className={cn(
      "fixed inset-0 z-50 flex items-center justify-center",
      "bg-gradient-to-br from-background via-background/95 to-background/90",
      "backdrop-blur-sm",
      className
    )}>
      {/* Gamified Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Gaming-inspired gradient orbs */}
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-purple-500/20 to-pink-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-blue-500/20 to-cyan-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-emerald-500/10 to-teal-500/5 rounded-full blur-3xl animate-pulse delay-500" />

        {/* Floating achievement particles */}
        {Array.from({ length: 12 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-yellow-400/60 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [-20, -40, -20],
              opacity: [0.3, 0.8, 0.3],
              scale: [0.8, 1.2, 0.8],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}

        {/* Gaming grid pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="w-full h-full" style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }} />
        </div>
      </div>

      {/* Gamified Skip Button */}
      {showSkipButton && currentPhase !== 'complete' && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="absolute top-6 right-6 z-10"
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSkip}
            className="text-muted-foreground hover:text-foreground bg-black/20 backdrop-blur-sm border border-white/10 rounded-full px-4 py-2 hover:bg-white/10 transition-all duration-300"
          >
            <SkipForward className="w-4 h-4 mr-2" />
            Skip Intro
          </Button>
        </motion.div>
      )}

      {/* XP Display */}
      {experiencePoints > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: "spring", damping: 15 }}
          className="absolute top-6 left-6 z-10"
        >
          <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 backdrop-blur-sm border border-yellow-500/30 rounded-full px-4 py-2 flex items-center gap-2">
            <Star className="w-4 h-4 text-yellow-400" />
            <span className="text-yellow-400 font-semibold text-sm">+{experiencePoints} XP</span>
          </div>
        </motion.div>
      )}



      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center justify-center space-y-8">
        <AnimatePresence mode="wait">
          {currentPhase === 'loading' && (
            <motion.div
              key="loading"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.2 }}
              transition={{ duration: 0.5 }}
              className="flex items-center justify-center"
            >
            </motion.div>
          )}

          {currentPhase === 'hello' && (
            <motion.div
              key="hello"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.8 }}
              className="flex flex-col items-center space-y-4"
            >
              <AppleHelloEnglishEffect
                className="h-16 md:h-20 lg:h-24 text-foreground"
                speed={1.2}
                onAnimationComplete={handleHelloComplete}
              />
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="text-sm text-muted-foreground text-center"
              >
                Welcome to SocialLoop
              </motion.p>
            </motion.div>
          )}

          {currentPhase === 'vietnamese' && (
            <motion.div
              key="vietnamese"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.8 }}
              className="flex flex-col items-center space-y-4"
            >
              <AppleHelloVietnameseEffect
                className="h-16 md:h-20 lg:h-24 text-foreground"
                speed={1}
                onAnimationComplete={handleVietnameseComplete}
              />
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="text-sm text-muted-foreground text-center"
              >
                AI-powered event networking platform
              </motion.p>
            </motion.div>
          )}

          {currentPhase === 'complete' && (
            <motion.div
              key="complete"
              initial={{ opacity: 1, scale: 1 }}
              animate={{ opacity: 0, scale: 0.95 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{
                duration: 1.2,
                ease: "easeInOut"
              }}
              className="flex flex-col items-center space-y-6"
            >
              {/* Achievement Unlock Animation */}
              {achievementUnlocked && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.5, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ type: "spring", damping: 12, delay: 0.2 }}
                  className="flex flex-col items-center space-y-3"
                >
                  <div className="relative">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                      className="w-16 h-16 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg shadow-yellow-500/50"
                    >
                      <Trophy className="w-8 h-8 text-white" />
                    </motion.div>
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="absolute -inset-2 rounded-full bg-yellow-400/20 blur-md"
                    />
                  </div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="text-center"
                  >
                    <div className="text-yellow-400 font-bold text-lg">Achievement Unlocked!</div>
                    <div className="text-white/80 text-sm">First Steps</div>
                  </motion.div>
                </motion.div>
              )}

              <motion.div
                initial={{ opacity: 0.8 }}
                animate={{ opacity: 0 }}
                transition={{ duration: 1.0, delay: 0.8 }}
                className="text-2xl font-light text-muted-foreground text-center"
              >
                Welcome to SocialLoop
                <div className="text-sm text-white/60 mt-2">Your networking adventure begins!</div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Gamified Progress Indicator */}
      {currentPhase !== 'complete' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="flex flex-col items-center space-y-3">
            {/* XP Progress Bar */}
            <div className="w-48 h-2 bg-black/30 rounded-full overflow-hidden backdrop-blur-sm border border-white/10">
              <motion.div
                initial={{ width: 0 }}
                animate={{
                  width: currentPhase === 'hello' ? '50%' :
                         currentPhase === 'vietnamese' ? '100%' : '0%'
                }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full relative"
              >
                <motion.div
                  animate={{ x: [-10, 10, -10] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent rounded-full"
                />
              </motion.div>
            </div>

            {/* Gaming-style dots */}
            <div className="flex space-x-3">
              {Array.from({ length: language === 'both' ? 2 : 1 }).map((_, index) => (
                <motion.div
                  key={index}
                  className={cn(
                    "w-3 h-3 rounded-full transition-all duration-500 relative",
                    (currentPhase === 'hello' && index === 0) ||
                    (currentPhase === 'vietnamese' && index === 1)
                      ? "bg-gradient-to-r from-blue-500 to-purple-500 shadow-lg shadow-blue-500/50"
                      : "bg-white/20 border border-white/30"
                  )}
                  animate={
                    (currentPhase === 'hello' && index === 0) ||
                    (currentPhase === 'vietnamese' && index === 1)
                      ? { scale: [1, 1.2, 1] }
                      : {}
                  }
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  {((currentPhase === 'hello' && index === 0) ||
                    (currentPhase === 'vietnamese' && index === 1)) && (
                    <motion.div
                      animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="absolute -inset-1 rounded-full bg-blue-400/30"
                    />
                  )}
                </motion.div>
              ))}
            </div>

            {/* Level indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.5 }}
              className="text-xs text-white/60 font-medium"
            >
              Level 1 • Getting Started
            </motion.div>
          </div>
        </motion.div>
      )}
    </div>
  )
}
